import {
  getAuthorizationHeader,
  getVisitorIdCookie,
  tryGetVisitorId,
} from "@brandclub/common-ui";
import { axiosRetailerSync } from "../../utils/axios";
import type {
  CheckoutSession,
  CheckoutSessionWithOrder,
  CheckoutSessionCreateRequest,
  CheckoutSessionUpdateRequest,
  CheckoutSessionCompleteRequest,
  DelegatePaymentRequest,
  DelegatePaymentResponse,
  Error as ApiError,
  Address,
  Buyer,
} from "../../components/Checkout/components/CheckoutSimplified/types";

export interface CheckoutSessionAutocompleteRequest {
  searchText: string;
  countryCodes: string[];
  location?: {
    latitude: number;
    longitude: number;
  };
  locationType?: string;
}

export interface CheckoutSessionAutocompleteResponse {
  status: "success" | "failed";
  autocompleteResult?: {
    description: string;
    place_id: string;
    structured_formatting: {
      main_text: string;
      secondary_text: string;
    };
    terms: {
      offset: number;
      value: string;
    }[];
    types: string[];
  }[];
  errorMessage?: string;
}

const API_VERSION = "2025-09-29";

async function getHeadersWithUniqueVisitorId(): Promise<
  Record<string, string>
> {
  const uniqueVisitorId = await tryGetVisitorId();
  const uniqueVisitorIdFromCookie = getVisitorIdCookie();

  const uniqueVisitorIdToUse =
    uniqueVisitorIdFromCookie && uniqueVisitorIdFromCookie !== ""
      ? uniqueVisitorIdFromCookie
      : uniqueVisitorId || "";
  return {
    ...(await getAuthorizationHeader()),
    "x-bc-visitor-id": uniqueVisitorIdToUse,
    "API-Version": API_VERSION,
    "Content-Type": "application/json",
  };
}

async function getHeaders() {
  return await getHeadersWithUniqueVisitorId();
}

function generateIdempotencyKey(): string {
  return `idem_${Date.now()}_${Math.random().toString(36).substring(7)}`;
}

// ============================================================
// CHECKOUT SESSION API
// ============================================================

export async function createCheckoutSession(
  request: CheckoutSessionCreateRequest,
  idempotencyKey?: string
): Promise<CheckoutSession> {
  const headers = await getHeaders();
  if (idempotencyKey) {
    headers["Idempotency-Key"] = idempotencyKey;
  }

  const response = await axiosRetailerSync.post<CheckoutSession>(
    "/checkout_sessions",
    request,
    { headers }
  );
  return response.data;
}

export async function getCheckoutSession(
  sessionId: string
): Promise<CheckoutSession | CheckoutSessionWithOrder> {
  const headers = await getHeaders();
  const response = await axiosRetailerSync.get<
    CheckoutSession | CheckoutSessionWithOrder
  >(`/checkout_sessions/${sessionId}`, { headers });
  return response.data;
}

export async function updateCheckoutSession(
  sessionId: string,
  request: CheckoutSessionUpdateRequest,
  idempotencyKey?: string
): Promise<CheckoutSession> {
  const headers = await getHeaders();
  if (idempotencyKey) {
    headers["Idempotency-Key"] = idempotencyKey;
  }

  const response = await axiosRetailerSync.post<CheckoutSession>(
    `/checkout_sessions/${sessionId}`,
    request,
    { headers }
  );
  return response.data;
}

export async function completeCheckoutSession(
  sessionId: string,
  request: CheckoutSessionCompleteRequest,
  idempotencyKey?: string
): Promise<CheckoutSessionWithOrder> {
  const headers = await getHeaders();
  // Idempotency is REQUIRED for completion to prevent duplicate orders
  headers["Idempotency-Key"] = idempotencyKey || generateIdempotencyKey();

  const response = await axiosRetailerSync.post<CheckoutSessionWithOrder>(
    `/checkout_sessions/${sessionId}/complete`,
    request,
    { headers }
  );
  return response.data;
}

export async function rememberCheckoutSession(
  sessionId: string
): Promise<CheckoutSession> {
  const headers = await getHeaders();
  const response = await axiosRetailerSync.post<CheckoutSession>(
    `/checkout_sessions/${sessionId}/remember`,
    {},
    { headers }
  );
  return response.data;
}

export async function cancelCheckoutSession(
  sessionId: string
): Promise<CheckoutSession> {
  const headers = await getHeaders();
  const response = await axiosRetailerSync.post<CheckoutSession>(
    `/checkout_sessions/${sessionId}/cancel`,
    {},
    { headers }
  );
  return response.data;
}

export async function autocompleteAddress(
  sessionId: string,
  request: CheckoutSessionAutocompleteRequest
): Promise<CheckoutSessionAutocompleteResponse> {
  const headers = await getHeaders();
  const response =
    await axiosRetailerSync.post<CheckoutSessionAutocompleteResponse>(
      `/checkout_sessions/${sessionId}/autocompleteAddress`,
      request,
      { headers }
    );
  return response.data;
}

// ============================================================
// DELEGATE PAYMENT API
// ============================================================

export async function delegatePayment(
  request: DelegatePaymentRequest,
  idempotencyKey?: string
): Promise<DelegatePaymentResponse> {
  const headers = await getHeaders();
  if (idempotencyKey) {
    headers["Idempotency-Key"] = idempotencyKey;
  }

  const response = await axiosRetailerSync.post<DelegatePaymentResponse>(
    "/agentic_commerce/delegate_payment",
    request,
    { headers }
  );
  return response.data;
}

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

export function isApiError(
  error: any
): error is { response: { data: ApiError } } {
  return error?.response?.data?.type && error?.response?.data?.code;
}

export function getErrorMessage(error: any): string {
  if (isApiError(error)) {
    return error.response.data.message;
  }
  return error?.message || "An unexpected error occurred";
}

// ============================================================
// CHECKOUT ORDER API
// ============================================================

export interface CheckoutOrderLineItem {
  id: string;
  name: string;
  quantity: number;
  unit_price: number;
  total: number;
}

export interface CheckoutOrderTotal {
  type: "subtotal" | "shipping" | "tax" | "total";
  display_text: string;
  amount: number;
}

export interface CheckoutOrder {
  id: string;
  checkout_session_id: string;
  permalink_url: string;
  status: string;
  created_at: string;
  updated_at: string;
  buyer: Buyer;
  fulfillment_address: Address;
  billing_address: Address;
  line_items: CheckoutOrderLineItem[];
  totals: CheckoutOrderTotal[];
  retailer: {
    id: number;
    name: string;
    display_name: string;
  };
}

export async function getCheckoutOrder(
  orderId: string
): Promise<CheckoutOrder> {
  const headers = await getHeaders();
  const response = await axiosRetailerSync.get<CheckoutOrder>(
    `/checkout_orders/${orderId}`,
    { headers }
  );
  return response.data;
}
